import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const SuperAdminPlanFeaturesMatrix = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [showImpactAnalysis, setShowImpactAnalysis] = useState(false);

  const [features, setFeatures] = useState([
    {
      id: 1,
      category: 'Business Intelligence',
      name: 'Dashboard Access',
      free: { enabled: true, limit: 'Basic' },
      growth: { enabled: true, limit: 'Advanced' },
      scale: { enabled: true, limit: 'Premium' },
      enterprise: { enabled: true, limit: 'Unlimited' }
    },
    {
      id: 2,
      category: 'Business Intelligence',
      name: 'Health Index',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: 'Monthly' },
      scale: { enabled: true, limit: 'Weekly' },
      enterprise: { enabled: true, limit: 'Real-time' }
    },
    {
      id: 3,
      category: 'Business Intelligence',
      name: 'AI Insights',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: '10/month' },
      scale: { enabled: true, limit: '100/month' },
      enterprise: { enabled: true, limit: 'Unlimited' }
    },
    {
      id: 4,
      category: 'Business Model Canvas',
      name: 'Canvas Creation',
      free: { enabled: true, limit: '1' },
      growth: { enabled: true, limit: '5' },
      scale: { enabled: true, limit: '20' },
      enterprise: { enabled: true, limit: 'Unlimited' }
    },
    {
      id: 5,
      category: 'Business Model Canvas',
      name: 'AI Suggestions',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: '50/month' },
      scale: { enabled: true, limit: '200/month' },
      enterprise: { enabled: true, limit: 'Unlimited' }
    },
    {
      id: 6,
      category: 'Business Model Canvas',
      name: 'PDF Export',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: 'Watermarked' },
      scale: { enabled: true, limit: 'Clean' },
      enterprise: { enabled: true, limit: 'Branded' }
    },
    {
      id: 7,
      category: 'Predictive Analysis',
      name: 'Manual Entry',
      free: { enabled: true, limit: 'Basic' },
      growth: { enabled: true, limit: 'Advanced' },
      scale: { enabled: true, limit: 'Advanced' },
      enterprise: { enabled: true, limit: 'Advanced' }
    },
    {
      id: 8,
      category: 'Predictive Analysis',
      name: 'Excel Upload',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: '10 rows' },
      scale: { enabled: true, limit: '100 rows' },
      enterprise: { enabled: true, limit: 'Unlimited' }
    },
    {
      id: 9,
      category: 'Predictive Analysis',
      name: 'Forecasting Depth',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: '3 months' },
      scale: { enabled: true, limit: '12 months' },
      enterprise: { enabled: true, limit: '36 months' }
    },
    {
      id: 10,
      category: 'Goal Intelligence',
      name: 'Goal Setting',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: '3 goals' },
      scale: { enabled: true, limit: '10 goals' },
      enterprise: { enabled: true, limit: 'Unlimited' }
    },
    {
      id: 11,
      category: 'Goal Intelligence',
      name: 'Feasibility Analysis',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: 'Basic' },
      scale: { enabled: true, limit: 'Advanced' },
      enterprise: { enabled: true, limit: 'AI-Powered' }
    },
    {
      id: 12,
      category: 'Goal Intelligence',
      name: 'Milestone Tracking',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: 'Quarterly' },
      scale: { enabled: true, limit: 'Monthly' },
      enterprise: { enabled: true, limit: 'Weekly' }
    },
    {
      id: 13,
      category: 'Reports & Analytics',
      name: 'Report Types',
      free: { enabled: true, limit: 'Basic' },
      growth: { enabled: true, limit: 'Standard' },
      scale: { enabled: true, limit: 'Advanced' },
      enterprise: { enabled: true, limit: 'Custom' }
    },
    {
      id: 14,
      category: 'Reports & Analytics',
      name: 'Export Formats',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: true, limit: 'PDF' },
      scale: { enabled: true, limit: 'PDF, Excel' },
      enterprise: { enabled: true, limit: 'All Formats' }
    },
    {
      id: 15,
      category: 'Reports & Analytics',
      name: 'Scheduling',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: false, limit: 'N/A' },
      scale: { enabled: true, limit: 'Weekly' },
      enterprise: { enabled: true, limit: 'Custom' }
    },
    {
      id: 16,
      category: 'Administrative Features',
      name: 'User Management',
      free: { enabled: true, limit: '1 user' },
      growth: { enabled: true, limit: '5 users' },
      scale: { enabled: true, limit: '20 users' },
      enterprise: { enabled: true, limit: 'Unlimited' }
    },
    {
      id: 17,
      category: 'Administrative Features',
      name: 'API Access',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: false, limit: 'N/A' },
      scale: { enabled: true, limit: 'Limited' },
      enterprise: { enabled: true, limit: 'Full' }
    },
    {
      id: 18,
      category: 'Administrative Features',
      name: 'Priority Support',
      free: { enabled: false, limit: 'N/A' },
      growth: { enabled: false, limit: 'N/A' },
      scale: { enabled: true, limit: 'Email' },
      enterprise: { enabled: true, limit: '24/7 Phone' }
    }
  ]);

  const plans = ['free', 'growth', 'scale', 'enterprise'];
  const categories = [...new Set(features?.map(f => f?.category))];

  const handleCellEdit = (featureId, plan, field, value) => {
    if (!editMode) return;
    setFeatures(features?.map(f => 
      f?.id === featureId 
        ? { ...f, [plan]: { ...f?.[plan], [field]: value } }
        : f
    ));
  };

  const handleToggleFeature = (featureId, plan) => {
    if (!editMode) return;
    setFeatures(features?.map(f => 
      f?.id === featureId 
        ? { ...f, [plan]: { ...f?.[plan], enabled: !f?.[plan]?.enabled } }
        : f
    ));
  };

  const handleSelectFeature = (featureId) => {
    setSelectedFeatures(prev => 
      prev?.includes(featureId) 
        ? prev?.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleBulkToggle = (plan, enabled) => {
    setFeatures(features?.map(f => 
      selectedFeatures?.includes(f?.id)
        ? { ...f, [plan]: { ...f?.[plan], enabled } }
        : f
    ));
    setSelectedFeatures([]);
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      console.log('Features saved:', features);
      setIsSaving(false);
      setEditMode(false);
    }, 1000);
  };

  const handleExport = () => {
    console.log('Exporting feature comparison...');
  };

  const renderCell = (feature, plan) => {
    const cell = feature?.[plan];
    
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-2">
        <button
          onClick={() => handleToggleFeature(feature?.id, plan)}
          disabled={!editMode}
          className={`p-1 rounded transition-smooth ${editMode ? 'hover:bg-muted' : ''}`}
        >
          <Icon
            name={cell?.enabled ? 'CheckCircle' : 'XCircle'}
            size={20}
            className={cell?.enabled ? 'text-success' : 'text-muted-foreground'}
          />
        </button>
        {editMode ? (
          <input
            type="text"
            value={cell?.limit}
            onChange={(e) => handleCellEdit(feature?.id, plan, 'limit', e?.target?.value)}
            className="w-full px-2 py-1 text-xs text-center bg-background border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        ) : (
          <span className="text-xs text-muted-foreground text-center">{cell?.limit}</span>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
              <Icon name="Grid" size={32} className="text-primary" />
              Plan Features Matrix
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
              Comprehensive feature comparison and management across all subscription plans
            </p>
          </div>

          {/* Action Bar */}
          <div className="mb-6 bg-card border border-border rounded-lg p-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {editMode && selectedFeatures?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {selectedFeatures?.length} selected
                    </span>
                    <div className="h-4 w-px bg-border" />
                    {plans?.map(plan => (
                      <div key={plan} className="flex gap-1">
                        <button
                          onClick={() => handleBulkToggle(plan, true)}
                          className="px-2 py-1 text-xs bg-success/10 text-success rounded hover:bg-success/20 transition-smooth"
                        >
                          Enable {plan}
                        </button>
                        <button
                          onClick={() => handleBulkToggle(plan, false)}
                          className="px-2 py-1 text-xs bg-destructive/10 text-destructive rounded hover:bg-destructive/20 transition-smooth"
                        >
                          Disable {plan}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowImpactAnalysis(!showImpactAnalysis)}
                  iconName="TrendingUp"
                  iconPosition="left"
                >
                  Impact Analysis
                </Button>
                {editMode ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditMode(false)}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleSave}
                      loading={isSaving}
                      iconName="Save"
                      iconPosition="left"
                    >
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    iconName="Edit"
                    iconPosition="left"
                  >
                    Edit Features
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Impact Analysis Panel */}
          {showImpactAnalysis && (
            <div className="mb-6 bg-card border border-border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="TrendingUp" size={20} className="text-primary" />
                Feature Impact Analysis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-background border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Free Plan Users</p>
                  <p className="text-2xl font-bold text-foreground">342</p>
                  <p className="text-xs text-success mt-1">+12% this month</p>
                </div>
                <div className="p-4 bg-background border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Growth Plan Users</p>
                  <p className="text-2xl font-bold text-foreground">487</p>
                  <p className="text-xs text-success mt-1">+8% this month</p>
                </div>
                <div className="p-4 bg-background border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Scale Plan Users</p>
                  <p className="text-2xl font-bold text-foreground">218</p>
                  <p className="text-xs text-success mt-1">+15% this month</p>
                </div>
                <div className="p-4 bg-background border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Enterprise Users</p>
                  <p className="text-2xl font-bold text-foreground">89</p>
                  <p className="text-xs text-success mt-1">+22% this month</p>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Matrix */}
          <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    {editMode && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase w-12">
                        <input
                          type="checkbox"
                          checked={selectedFeatures?.length === features?.length}
                          onChange={(e) => setSelectedFeatures(e?.target?.checked ? features?.map(f => f?.id) : [])}
                          className="rounded border-border"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Feature</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Free</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Growth</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Scale</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {categories?.map(category => (
                    <React.Fragment key={category}>
                      {features?.filter(f => f?.category === category)?.map((feature, idx) => (
                        <tr key={feature?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                          {editMode && (
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedFeatures?.includes(feature?.id)}
                                onChange={() => handleSelectFeature(feature?.id)}
                                className="rounded border-border"
                              />
                            </td>
                          )}
                          <td className="px-4 py-3">
                            {idx === 0 && (
                              <span className="text-sm font-semibold text-primary">{category}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground">{feature?.name}</td>
                          <td className="px-4 py-3">{renderCell(feature, 'free')}</td>
                          <td className="px-4 py-3">{renderCell(feature, 'growth')}</td>
                          <td className="px-4 py-3">{renderCell(feature, 'scale')}</td>
                          <td className="px-4 py-3">{renderCell(feature, 'enterprise')}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {categories?.map(category => (
              <div key={category} className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-primary mb-4">{category}</h3>
                <div className="space-y-4">
                  {features?.filter(f => f?.category === category)?.map(feature => (
                    <div key={feature?.id} className="border border-border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-foreground">{feature?.name}</h4>
                        {editMode && (
                          <input
                            type="checkbox"
                            checked={selectedFeatures?.includes(feature?.id)}
                            onChange={() => handleSelectFeature(feature?.id)}
                            className="rounded border-border"
                          />
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {plans?.map(plan => (
                          <div key={plan}>
                            <p className="text-xs text-muted-foreground mb-1 capitalize">{plan}</p>
                            {renderCell(feature, plan)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPlanFeaturesMatrix;